import { Hash } from "lucide-react";
import type { ReactNode } from "react";
import { Button } from "../atoms/Button";
import { Link } from "../atoms/Link";
import { cn } from "../lib/cn";
import { EmptyState } from "../molecules/EmptyState";
import { ErrorState } from "../molecules/ErrorState";
import { Pagination } from "../molecules/Pagination";
import { SearchField } from "../molecules/SearchField";
import { Skeleton } from "../molecules/Skeleton";
import { EventTimeline } from "../organisms/EventTimeline";
import type { EventTimelineItem } from "../organisms/EventTimeline";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../primitives/Select";

export interface EventTypeOption {
	value: string;
	label: string;
}

export interface RespaCodeOption {
	value: string;
	label: string;
}

export interface EventSearchPageProps {
	searchValue: string;
	onSearchChange: (value: string) => void;
	results: EventTimelineItem[];
	pagination: { page: number; pageCount: number };
	onPageChange: (page: number) => void;
	loading?: boolean;
	error?: Error | null;
	onSelectEvent: (hash: string) => void;
	hasSearched?: boolean;
	className?: string;
	eventType?: string | undefined;
	onEventTypeChange?: (v: string | undefined) => void;
	respaCode?: string | undefined;
	onRespaCodeChange?: (v: string | undefined) => void;
	eventTypeOptions?: EventTypeOption[];
	respaCodeOptions?: RespaCodeOption[];
}

function EventSearchPage({
	searchValue,
	onSearchChange,
	results,
	pagination,
	onPageChange,
	loading = false,
	error = null,
	onSelectEvent,
	hasSearched = false,
	className,
	eventType,
	onEventTypeChange,
	respaCode,
	onRespaCodeChange,
	eventTypeOptions = [],
	respaCodeOptions = [],
}: EventSearchPageProps): ReactNode {
	const eventsWithView: EventTimelineItem[] = results.map((e) => ({
		...e,
		onView: () => onSelectEvent(e.hash),
	}));

	const showFilters =
		onEventTypeChange !== undefined && onRespaCodeChange !== undefined;
	const hasActiveFilter = Boolean(eventType) || Boolean(respaCode);

	function handleClearFilters(): void {
		onEventTypeChange?.(undefined);
		onRespaCodeChange?.(undefined);
	}

	return (
		<div className={cn("flex flex-col gap-4", className)}>
			<SearchField
				value={searchValue}
				onChange={onSearchChange}
				placeholder="Find an event by its reference, or look up the loan instead"
				onClear={() => onSearchChange("")}
			/>

			{showFilters && (
				<div className="flex flex-wrap items-center gap-2">
					<Select
						value={eventType ?? ""}
						onValueChange={(v) => onEventTypeChange(v || undefined)}
					>
						<SelectTrigger className="h-8 w-56 text-xs">
							<SelectValue placeholder="All event types" />
						</SelectTrigger>
						<SelectContent>
							{eventTypeOptions.map((opt) => (
								<SelectItem
									key={opt.value}
									value={opt.value}
									className="text-xs"
								>
									{opt.label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>

					<Select
						value={respaCode ?? ""}
						onValueChange={(v) => onRespaCodeChange(v || undefined)}
					>
						<SelectTrigger className="h-8 w-64 text-xs">
							<SelectValue placeholder="All RESPA categories" />
						</SelectTrigger>
						<SelectContent>
							{respaCodeOptions.map((opt) => (
								<SelectItem
									key={opt.value}
									value={opt.value}
									className="text-xs"
								>
									{opt.label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>

					{hasActiveFilter && (
						<Button variant="ghost" size="sm" onClick={handleClearFilters}>
							Clear filters
						</Button>
					)}
				</div>
			)}

			{error ? (
				<ErrorState error={error} title="Failed to load events" />
			) : loading ? (
				<div className="flex flex-col gap-3">
					{Array.from({ length: 5 }).map((_, i) => {
						// biome-ignore lint/suspicious/noArrayIndexKey: skeleton placeholders
						return <Skeleton key={i} className="h-32 w-full" />;
					})}
				</div>
			) : hasSearched ? (
				<>
					<EventTimeline
						events={eventsWithView}
						emptyMessage="No event matches that reference. Try looking up the loan instead."
					/>
					<Pagination
						page={pagination.page}
						pageCount={pagination.pageCount}
						onPageChange={onPageChange}
						className="mt-4 justify-center"
					/>
				</>
			) : (
				<EmptyState
					title="Search for an event"
					description="Have an event reference? Paste it above to verify it. No reference? You can also reach any event by looking up its loan."
					icon={<Hash className="h-12 w-12" aria-hidden="true" />}
					action={<Link href="/loans">Look up the loan instead</Link>}
				/>
			)}
		</div>
	);
}
EventSearchPage.displayName = "EventSearchPage";

export default EventSearchPage;
export { EventSearchPage };
